/**
 * Case Processing Status Constants
 *
 * These arrays define the case processing statuses that are considered
 * "in progress" or "failed" states, used for rendering spinners and error icons
 * in the case list table.
 */

/** Case statuses that indicate processing is in progress */
export const CASE_PROCESSING_STATUSES = [
  'Cloning',
  'AI Processing Requested',
  'Configuring AI Pipeline',
  'AI Pagination Processing',
  'Medical Match Processing',
  'AI Timeline Processing',
] as const;

/** Case statuses that indicate a failure */
export const CASE_FAILED_STATUSES = [
  'AI Timeline Failed',
  'Cloning Failed',
  'Preprocessing Error',
  'AI Pagination Failed',
] as const;

/** Case statuses that block document deletion */
export const CASE_DELETE_BLOCKED_STATUSES = [
  'Configuring AI Pipeline',
  'AI Pagination Processing',
  'Medical Match Processing',
] as const;

/** Hover explanation shown on the disabled delete-document button */
export const CASE_DELETE_BLOCKED_TOOLTIP = 'Cannot delete document while AI processing is in progress';

/**
 * Raw (server-side) case status keys — the uppercase snake_case values the
 * backend returns on `CaseStatusOption.raw`. Kept separate from the display-
 * label constants above so callers that compare against `raw` don't have to
 * hard-code a magic string.
 */
export const CASE_STATUS_RAW = {
  READY_FOR_REVIEW: 'READY_FOR_REVIEW',
} as const;

/**
 * Default set of case statuses that should be *visible* on page load —
 * everything except the two hidden-by-default statuses (Closed and
 * Awaiting CMS Closure). Used by the Medical Records nav link so the page
 * opens pre-filtered via `?caseStatus=...` — the existing URL-sync effect
 * on that page already turns URL params into the selected filter state,
 * so seeding the URL is the simplest reliable way to seed the filter.
 */
export const DEFAULT_VISIBLE_CASE_STATUSES = [
  'NEW',
  'CLONING',
  'CLONING_FAILED',
  'DOCUMENT_UPLOAD',
  'PREPROCESSING_ERROR',
  'READY_FOR_AI_PROCESSING',
  'AI_PROCESSING_REQUESTED',
  'CONFIGURING_AI_PIPELINE',
  'AI_PAGINATION_PROCESSING',
  'AI_PAGINATION_FAILED',
  'AI_PAGINATION_COMPLETE',
  'MEDICAL_MATCH_PROCESSING',
  'MEDICAL_MATCH_COMPLETE',
  'AI_TIMELINE_PROCESSING',
  'AI_TIMELINE_FAILED',
  'AI_TIMELINE_COMPLETE',
  'READY_FOR_REVIEW',
] as const;
